import { menuItemFeedQueryOptions } from '@/api/queries/activity';
import { businessDetailQueryOptions } from '@/api/queries/businesses';
import { menuItemFriendRecommendationsQueryOptions } from '@/api/queries/friend-recommendation';
import { menuItemDetailQueryOptions } from '@/api/queries/menu-items';
import { useNotesByMenuItem } from '@/features/notes/hooks/use-notes';
import { useAuth } from '@/hooks/use-auth';
import type { FeedItem, RatingDistribution } from '@/types/feed';
import type { MenuItem } from '@/types/menu-item';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

export function computeAggregateDistribution(
  feedData: { pages: { items: { ratingDistribution: RatingDistribution | null }[] }[] } | undefined,
): RatingDistribution | null {
  if (!feedData) return null;
  for (const page of feedData.pages) {
    for (const item of page.items) {
      if (item.ratingDistribution) {
        return { ...item.ratingDistribution };
      }
    }
  }
  return null;
}

export function computeTotalRatings(distribution: RatingDistribution | null): number {
  if (!distribution) return 0;
  return distribution.notForMe + distribution.okay + distribution.tryIt + distribution.mustTry;
}

export function computeHeroImageUrl(
  menuItem: Pick<MenuItem, 'coverPhotoUrl'> | undefined,
  feedItems: Pick<FeedItem, 'photos'>[],
): string | null {
  if (menuItem?.coverPhotoUrl) return menuItem.coverPhotoUrl;
  for (const item of feedItems) {
    if (item.photos.length > 0) return item.photos[0].imageUrl;
  }
  return null;
}

export function useMenuItemDetailData(id: string | undefined) {
  const { isLoggedIn } = useAuth();

  const {
    data: menuItem,
    isLoading: isLoadingDetail,
    error: detailError,
    refetch: refetchDetail,
  } = useQuery(menuItemDetailQueryOptions(id || ''));

  const { data: business } = useQuery({
    ...businessDetailQueryOptions(menuItem?.businessId || ''),
    enabled: !!menuItem?.businessId,
  });

  const { data: recommendationsData } = useQuery({
    ...menuItemFriendRecommendationsQueryOptions(id || ''),
    enabled: isLoggedIn && !!id,
  });

  const {
    data: feedData,
    isLoading: isLoadingFeed,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery(menuItemFeedQueryOptions(id || ''));

  const { data: notesData, isLoading: isLoadingNotes } = useNotesByMenuItem(id || '');

  const feedItems = feedData?.pages.flatMap((page) => page.items) ?? [];
  const recommendations = recommendationsData?.recommendations ?? [];
  const notes = notesData?.items ?? [];
  const aggregateDistribution = computeAggregateDistribution(feedData);
  const totalRatings = computeTotalRatings(aggregateDistribution);
  const heroImageUrl = computeHeroImageUrl(menuItem, feedItems);

  return {
    menuItem,
    business,
    recommendations,
    feedItems,
    notes,
    aggregateDistribution,
    totalRatings,
    heroImageUrl,
    isLoadingDetail,
    detailError,
    refetchDetail,
    isLoadingFeed,
    isLoadingNotes,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isLoggedIn,
  };
}
